import HBIcon from "@/components/HBIcon";
import { BuyVipDialog } from "@/components/Vip";
import { EVENT } from "@/constants/event";
import { isBigCompanyByUser } from "@/constants/vip";
import useAppStore from "@/context/store";
import { getDeliveryDetail } from "@/services";
import { handleDownload } from "@/utils/download";
import AppEvent from "@/utils/event";
import getTimeDuration from "@/utils/getTimeDuration";
import useRequest from "@/utils/useReq";
import { useSessionStorageState, useSetState } from "ahooks";
import { Alert, Button } from "antd";
import dayjs from "dayjs";
import { at } from "lodash";
import { useRouter } from "next/router";
import qs from "qs";
import { useEffect, useMemo, useRef } from "react";
import { When } from "react-if";
import UserGuideTooltip from "../UserGuideTooltip";
import styles from "./index.module.less";

// 花瓣素材
export default function PinMaterial({ pin }) {
  const router = useRouter();
  const downloadDialogRef = useRef();

  const { vips, user, config } = useAppStore(({ vips, user, config }) => ({
    vips,
    user,
    config,
  }));

  const info = pin.material_info;
  const material = pin.file_material;
  const [downloadBubble, setDownloadBubble] = useSetState({
    visible: false,
    props: {},
  });

  const [expireHasShow, setExpireHasShow] =
    useSessionStorageState("expireAlertHasShow");
  // 到期提醒
  const expireTip = useMemo(() => {
    if (!vips?.length || isBigCompanyByUser(user)) {
      return false;
    }

    const days = dayjs(vips[0]?.expires_at).diff(dayjs(), "d", true);
    if (days <= (config?.expireTipDays || -1)) {
      const content = (
        <div>
          素材VIP将在{getTimeDuration(vips[0]?.expires_at, "后", true)}到期{" "}
          <a
            className="primary-text-btn"
            onClick={() => {
              BuyVipDialog.open();
              setExpireHasShow(true);
            }}
          >
            立即续费
          </a>
        </div>
      );
      return {
        days,
        content,
      };
    }
    return false;
  }, [config?.expireTipDays, setExpireHasShow, user, vips]);

  const { data: downloadTip = "" } = useRequest(async () => {
    const result = await getDeliveryDetail("detail_download_label");
    return result?.pit_modules?.[0]?.material?.title || "";
  });

  const files = useMemo(() => {
    const info = pin.material_info;
    const material = pin.file_material;

    let files = [];

    if (
      material.material_category === "平面模板" ||
      material.material_category === "视频模板"
    ) {
      const file = info?.files?.find((file) => file.file_type === "ORIGIN");
      if (file) {
        files.push(file);
      }
    } else if (material.material_category === "视频") {
      const mps = info?.content?.mps?.find((mps) => {
        return mps.name === "HD";
      });
      if (mps) {
        files.push({
          file_format: mps.file_format,
          file_size: mps.file_size,
          file_type: mps.name,
        });
      }
    } else {
      files = info.files;
    }

    return files;
  }, [pin]);

  const size = useMemo(() => {
    const material = pin.file_material;
    const info = pin.material_info;
    const content = info?.content;

    if (material.material_category === "平面模板") {
      if (content && content.layouts) {
        return {
          width: content.layouts[0].width,
          height: content.layouts[0].height,
        };
      } else {
        return {
          width: info.preview.width,
          height: info.preview.height,
        };
      }
    } else if (
      material.material_category === "图片" &&
      // 兼容一批背景图逻辑
      info.type !== "background"
    ) {
      // 兼容AIGC图片
      if (info?.authorization?.name?.endsWith("AIGC")) {
        return {
          width: info.preview.width,
          height: info.preview.height,
        };
      } else {
        if (content.originWidth && content.originHeight) {
          return {
            width: content.originWidth,
            height: content.originHeight,
          };
        }
      }
    }

    return null;
  }, [pin]);

  const copyright = useMemo(() => {
    if (
      material.material_category === "图片" ||
      material.material_category === "视频"
    ) {
      const copyright = at(pin, "material_info.content.extra.copyright")[0];

      return copyright || "";
    }

    // 其他默认已获得
    return "已获得著作权/肖像权授权";
  }, [material.material_category, pin]);

  // 署名
  // const name = useMemo(() => {
  //   return at(pin, "material_info.content.extra.credit_line")[0];
  // }, [pin]);
  const button = useMemo(() => {
    return files.map((file) => {
      const size = file.file_size;
      const format = file.file_format?.toUpperCase();
      return (
        <Button
          className={styles.button}
          type="primary"
          icon={<HBIcon type="xiazai" />}
          onClick={() => handleDownload(pin, file)}
          data-gd-click="button_click"
          data-button-name="下载"
          data-board-id={pin?.board_id}
          data-content-id={pin.id}
          key={file.file_type}
          id="material_download_btn"
        >
          <div className={styles.container}>
            <div className={styles.text}>下载{format}</div>
            <When condition={!!Number(size)}>
              <div className={styles.subtext}>
                {(size / (1024 * 1024)).toFixed(2)}M
              </div>
            </When>
          </div>
          <When condition={downloadTip && files.length === 1}>
            <div className={styles.label}>{downloadTip}</div>
          </When>
        </Button>
      );
    });
  }, [files, pin, downloadTip]);

  useEffect(() => {
    if (router.query.pinId != pin?.pin_id) {
      // 切换页面时，pin获取是异步的，所以需要等pin获取到了再执行
      return;
    }

    const clickDownload =
      router.query.clickDownload ||
      qs.parse(location.search.slice(1))?.clickDownload;

    // fix: 解决跳转到没有下载的素材时，下载弹窗不消失的问题
    if (downloadDialogRef.current?.destroy) {
      downloadDialogRef.current.destroy();
    }

    if (clickDownload && files[clickDownload]) {
      handleDownload(pin, files[clickDownload]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.clickDownload, pin?.pin_id]);

  useEffect(() => {
    const onPinTimesN = async (getData) => {
      const data = await getData();
      if (data) {
        setDownloadBubble({
          visible: true,
          props: data,
        });
      }
    };
    AppEvent.on(EVENT.PIN_MATERIAL_TIME_N, onPinTimesN);

    return () => {
      AppEvent.off(EVENT.PIN_MATERIAL_TIME_N, onPinTimesN);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (files.length <= 0) {
    return null;
  }

  return (
    <div className={styles.material}>
      {expireTip && !expireHasShow && (
        <Alert
          className={styles.expireAlert}
          message={expireTip.content}
          onClose={() => {
            setExpireHasShow(true);
          }}
          type="error"
          icon={<HBIcon type="ic_info" />}
          showIcon
          closable
          closeIcon={<HBIcon type="ic_clear2" />}
        />
      )}
      <div className={styles.top}>
        <a
          className={styles.left}
          data-gd-click="button_click"
          data-button-name="源文件可下载"
          data-board-id={pin?.board_id}
          data-content-id={pin?.pin_id}
          data-material-id={pin?.material_info?.id}
          href="/vip/info"
          target="_blank"
        >
          <div className={styles.name}>花瓣素材</div>
          <div className={styles.link}>
            <span className={styles.text}>{config?.materialDetailText}</span>
            <HBIcon className={styles.icon} type="ic_Next21" />
          </div>
        </a>
        <div className={styles.right}>
          <div className={styles.id}>ID:{info.id}</div>
        </div>
      </div>
      <div className={styles.param}>
        <div className={styles.item}>
          <div className={styles.label}>素材分类</div>
          <div className={styles.content}>
            {material.material_category === "高端免抠"
              ? "免抠元素"
              : material.material_category}
          </div>
        </div>
        <When condition={size}>
          <div className={styles.item}>
            <div className={styles.label}>素材规格</div>
            <div className={styles.content}>
              {size?.width}x{size?.height}px
            </div>
          </div>
        </When>
        <When condition={material.material_category === "视频"}>
          <div className={styles.item}>
            <div className={styles.label}>
              分<span className={styles.space3}></span>辨
              <span className={styles.space3}></span>率
            </div>
            <div className={styles.content}>1080p</div>
          </div>
        </When>
        <When condition={info?.authorization?.name?.endsWith("AIGC")}>
          <div className={styles.item}>
            <div className={styles.label}>创作来源</div>
            <div className={styles.content}>
              设计师基于自有创意使用AI绘图工具创造
            </div>
          </div>
        </When>
      </div>
      <div className={styles.copyright}>
        <div className={styles.icon}></div>
        <div className={styles.text}>
          {copyright}可用于
          <span>个人用途/企业商业用途</span>
          {material.material_category === "平面模板" ? "；未获得字体授权" : ""}
        </div>
      </div>

      <UserGuideTooltip
        visible={downloadBubble.visible}
        localStorageKey={(id) => `hb_material_download_bubble_${id}`}
        {...downloadBubble.props}
      >
        <div className={styles.download}>{button}</div>
      </UserGuideTooltip>
    </div>
  );
}

export function PinMaterialRedirect({ pin }) {
  const { config } = useAppStore(({ config }) => ({
    config,
  }));

  return (
    <div className={styles.material}>
      <div className={styles.top}>
        <a
          className={styles.left}
          data-gd-click="button_click"
          data-button-name="源文件可下载"
          data-board-id={pin?.board_id}
          data-content-id={pin?.pin_id}
          data-material-id={pin?.file_material?.material_id}
          href="/vip/info"
          target="_blank"
        >
          <div className={styles.name}>花瓣素材</div>
          <div className={styles.link}>
            <span className={styles.text}>{config?.materialDetailText}</span>
            <HBIcon className={styles.icon} type="ic_Next21" />
          </div>
        </a>
      </div>
      <a
        className={styles.redirect}
        href={`/pins/${pin?.file_material?.pin_id}`}
        target="_blank"
        rel="noreferrer"
      >
        <div className={styles.content}>
          <HBIcon className={styles.icon} type="error_outline" />
          <div className={styles.text}>当前素材已更新，前往最新版查看下载</div>
        </div>
        <HBIcon className={styles.arrow} type="ic_chevron_right" />
      </a>
    </div>
  );
}
